import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import NavigationDetailContent from "./NavigationDetailContent";

// Expanded navigation view that renders the full grouped menu and footer in one detail pane.
const ExpandedSideNav = ({
  navigationSearch,
  setNavigationSearch,
  isSearching,
  topLevelItems,
  utilityItems,
  renderNavigationDescriptor,
  filterNavigationDescriptors,
  syncAutoExpandedMenuIds,
}) => {
  const { t } = useTranslation();

  const filteredPrimaryNavigation = useMemo(
    () => filterNavigationDescriptors(topLevelItems),
    [filterNavigationDescriptors, topLevelItems]
  );
  const filteredUtilityNavigation = useMemo(
    () => filterNavigationDescriptors(utilityItems),
    [filterNavigationDescriptors, utilityItems]
  );

  const primaryGroups = useMemo(() => {
    const sectionOrder = [t("Trading"), "", t("Reporting"), t("Tools"), t("Operations")];
    const sectionLabels = [
      ...sectionOrder,
      ...filteredPrimaryNavigation.items
        .map((item) => item.sectionLabel || "")
        .filter((label) => !sectionOrder.includes(label)),
    ];

    return sectionLabels
      .map((label) => ({
        key: `primary-${label || "default"}`,
        label,
        items: filteredPrimaryNavigation.items
          .filter((item) => (item.sectionLabel || "") === label)
          .map(renderNavigationDescriptor)
          .filter(Boolean),
      }))
      .filter((section) => section.items.length > 0);
  }, [filteredPrimaryNavigation.items, renderNavigationDescriptor, t]);

  const footerGroups = useMemo(() => {
    const footerItems = filteredUtilityNavigation.items
      .map(renderNavigationDescriptor)
      .filter(Boolean);

    if (footerItems.length === 0) {
      return [];
    }

    return [
      {
        key: "utility",
        label: "",
        items: footerItems,
      },
    ];
  }, [filteredUtilityNavigation.items, renderNavigationDescriptor]);

  const autoExpandedMenuIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...filteredPrimaryNavigation.autoExpandedMenuIds,
          ...filteredUtilityNavigation.autoExpandedMenuIds,
        ])
      ),
    [
      filteredPrimaryNavigation.autoExpandedMenuIds,
      filteredUtilityNavigation.autoExpandedMenuIds,
    ]
  );

  useEffect(() => {
    syncAutoExpandedMenuIds(autoExpandedMenuIds);
  }, [autoExpandedMenuIds, syncAutoExpandedMenuIds]);

  return (
    <NavigationDetailContent
      searchLabel={t("NavigationSearch")}
      searchValue={navigationSearch}
      onSearchChange={setNavigationSearch}
      groups={primaryGroups}
      footerGroups={footerGroups}
      isSearching={isSearching}
      emptyStateLabel={t("NoMatchingNavigationItems")}
    />
  );
};

export default ExpandedSideNav;

